import { useState } from "react";
import axios from "axios";
import { Camera, X, Upload, Mic, Headphones, Volume2 } from "lucide-react";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

const AUDIO_EXTS = ["ogg", "mp3", "m4a", "wav"];
const VIDEO_EXTS = ["mp4", "mov", "m4v", "webm"];

const extOf = (url) => (url || "").split("?")[0].split("#")[0].split(".").pop().toLowerCase();
const isAudioUrl = (url) => AUDIO_EXTS.includes(extOf(url));
const isVideoUrl = (url) => VIDEO_EXTS.includes(extOf(url));

// Base64-url-encode precis som backendens _safe_url_key
export const safeUrlKey = (url) => {
  const b = btoa(unescape(encodeURIComponent(url)));
  return b.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/**
 * Admin-editor för galleri med stöd för bilder, videor och röstbeskrivningar.
 * Props:
 *   collection: "tjanstekort" | "foreningar" | "evenemang"
 *   itemId: string
 *   bilder: string[] (URL-lista)
 *   galleriAudio: { [safeKey]: audio_url }   – valfri
 *   onChange(newList): uppdaterar bilder-listan
 *   onAudioChange(newMap): uppdaterar galleri_audio-mappen
 */
export default function GalleriEditor({ collection, itemId, bilder = [], galleriAudio = {}, onChange, onAudioChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [recordingFor, setRecordingFor] = useState(null); // image_url för vilken vi just spelar in

  const MAX_GALLERI = 3;
  const atLimit = (bilder || []).length >= MAX_GALLERI;

  const pushUrl = (url) => onChange?.([...(bilder || []), url]);

  const handleUploadMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/mod/${collection}/${itemId}/galleri`,
        fd,
        { headers: { ...modHeaders(), "Content-Type": "multipart/form-data" } }
      );
      pushUrl(data.url);
    } catch (err) {
      setError(err.response?.data?.detail || "Uppladdning misslyckades");
    }
    setUploading(false);
    e.target.value = "";
  };

  // Ren ljudfil – via /api/upload (publik) eller via /mod-galleri-endpoint.
  // Vi använder galleri-endpointen så filen hamnar i listan som en egen entry.
  const handleUploadAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/mod/${collection}/${itemId}/galleri`,
        fd,
        { headers: { ...modHeaders(), "Content-Type": "multipart/form-data" } }
      );
      pushUrl(data.url);
    } catch (err) {
      setError(err.response?.data?.detail || "Uppladdning misslyckades");
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleDelete = async (url) => {
    if (!window.confirm("Ta bort denna post från galleriet?")) return;
    try {
      await axios.delete(
        `${API}/mod/${collection}/${itemId}/galleri`,
        { headers: modHeaders(), params: { url } }
      );
      onChange?.((bilder || []).filter(u => u !== url));
      // Backend rensar galleri_audio själv, spegla lokalt:
      if (galleriAudio && galleriAudio[safeUrlKey(url)]) {
        const m = { ...galleriAudio };
        delete m[safeUrlKey(url)];
        onAudioChange?.(m);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Kunde inte ta bort");
    }
  };

  const attachAudio = async (imageUrl, audioUrl) => {
    try {
      await axios.post(
        `${API}/mod/${collection}/${itemId}/galleri/audio`,
        { image_url: imageUrl, audio_url: audioUrl },
        { headers: modHeaders() }
      );
      const m = { ...(galleriAudio || {}) };
      if (audioUrl) m[safeUrlKey(imageUrl)] = audioUrl;
      else delete m[safeUrlKey(imageUrl)];
      onAudioChange?.(m);
    } catch (err) {
      setError(err.response?.data?.detail || "Kunde inte spara röstbeskrivning");
    }
  };

  const src = (u) => (u && u.startsWith("/")) ? `${BACKEND_URL}${u}` : u;

  return (
    <div data-testid={`galleri-editor-${itemId}`} className="mt-3 border-t border-warm-border pt-3">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="text-xs font-semibold text-bark uppercase tracking-wider flex items-center gap-1.5">
          <Camera size={13} /> Galleri ({(bilder || []).length} / {MAX_GALLERI})
        </p>
        <div className="flex items-center gap-3">
          <label className={`inline-flex items-center gap-1.5 text-xs font-semibold ${atLimit ? "text-bark/40 cursor-not-allowed" : "text-falu hover:text-falu-hover cursor-pointer"}`}>
            {uploading ? "Laddar upp..." : (<><Upload size={13} /> Lägg till bild / video</>)}
            <input type="file" accept="image/*,video/mp4,video/quicktime" onChange={handleUploadMedia} disabled={uploading || atLimit} className="hidden" data-testid={`galleri-upload-${itemId}`} />
          </label>
          <label className={`inline-flex items-center gap-1.5 text-xs font-semibold ${atLimit ? "text-bark/40 cursor-not-allowed" : "text-pine hover:text-pine-hover cursor-pointer"}`}>
            <Headphones size={13} /> Lägg till ljud
            <input type="file" accept="audio/*,.mp3,.ogg,.wav,.m4a" onChange={handleUploadAudio} disabled={uploading || atLimit} className="hidden" data-testid={`galleri-audio-upload-${itemId}`} />
          </label>
        </div>
      </div>
      {atLimit && (
        <p data-testid={`galleri-limit-${itemId}`} className="text-xs text-falu mb-2 font-semibold">
          Max {MAX_GALLERI} bilder per post nått. Ta bort en innan du laddar upp ny.
        </p>
      )}
      {error && <p className="text-xs text-falu mb-2">{error}</p>}
      {(bilder || []).length === 0 ? (
        <p className="text-xs text-bark/50">Inga inlägg än.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {bilder.map((url, i) => {
            const audioCompanion = galleriAudio?.[safeUrlKey(url)] || null;
            const isAudio = isAudioUrl(url);
            const isVideo = isVideoUrl(url);
            return (
              <div key={url + i} className="relative rounded-sm overflow-hidden border border-warm-border bg-ink">
                <div className="aspect-square flex items-center justify-center bg-ink">
                  {isAudio ? (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-pine/15 p-2">
                      <Volume2 size={28} className="text-parchment/80" />
                      <audio
                        src={src(url)}
                        controls
                        preload="none"
                        className="w-full"
                        data-testid={`galleri-audio-player-${i}`}
                      />
                    </div>
                  ) : isVideo ? (
                    <video
                      src={src(url) + "#t=0.5"}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img src={src(url)} alt="" className="w-full h-full object-cover" />
                  )}
                </div>

                <button
                  onClick={() => handleDelete(url)}
                  className="absolute top-1 right-1 bg-ink/80 hover:bg-falu text-parchment p-1 rounded-full transition-colors"
                  title="Ta bort"
                  data-testid={`galleri-delete-${itemId}-${i}`}
                >
                  <X size={12} />
                </button>

                {/* Röstbeskrivning kan knytas till bild/video (inte till rena audio-entries) */}
                {!isAudio && (
                  <div className="bg-bone-alt/80 border-t border-warm-border p-1.5">
                    {recordingFor === url ? (
                      <div>
                        <VoiceNoteRecorder
                          value={audioCompanion}
                          onChange={(audioUrl) => attachAudio(url, audioUrl)}
                        />
                        <button
                          onClick={() => setRecordingFor(null)}
                          className="mt-1 text-[10px] text-bark/70 hover:text-ink underline"
                          data-testid={`galleri-voice-close-${i}`}
                        >
                          Stäng
                        </button>
                      </div>
                    ) : audioCompanion ? (
                      <div className="flex items-center gap-1.5">
                        <Mic size={11} className="text-pine" />
                        <audio src={src(audioCompanion)} controls preload="none" className="h-6 flex-1 min-w-0" />
                        <button
                          onClick={() => attachAudio(url, null)}
                          className="text-[10px] text-falu hover:text-falu-hover underline shrink-0"
                          data-testid={`galleri-voice-remove-${i}`}
                        >
                          Ta bort
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setRecordingFor(url)}
                        className="flex items-center gap-1 text-[10px] font-semibold text-pine hover:text-pine-hover"
                        data-testid={`galleri-voice-add-${i}`}
                      >
                        <Mic size={11} /> Lägg till röstbeskrivning
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <p className="mt-2 text-[11px] text-bark/60 italic">
        Tips: röstbeskrivningar hjälper synskadade besökare att uppleva bilderna.
      </p>
    </div>
  );
}
